import type { ArtPalette } from "./palette.js";
import type { Grid, Picture } from "./picture.js";

/** How much each cell has been lifted by the pointer, and which way it leans. */
export interface Ripple {
  readonly energy: Float32Array;
  readonly angle: Float32Array;
}

/** Half-life of a lifted cell, as a rate: it settles in about a second. */
const SETTLE = 2.6;

/**
 * Draws every lifted cell over the picture and lets it settle a little.
 *
 * A lifted cell darkens where it stood and lifts away from the pointer as a
 * leaf one step brighter, with a pale tip — or, in dots, a short stroke.
 * Returns whether anything is still lifted, so the canvas knows to keep
 * drawing.
 */
export function drawRipple(
  ctx: CanvasRenderingContext2D,
  layer: {
    readonly grid: Grid;
    readonly picture: Picture;
    readonly palette: ArtPalette;
    readonly ripple: Ripple;
  },
  dt: number,
): boolean {
  const { grid, picture, palette, ripple } = layer;
  const { energy, angle } = ripple;
  const last = palette.ramp.length - 1;
  let any = false;

  // Where each lifted cell stood: a step darker, or the background for dots.
  for (let i = 0; i < energy.length; i++) {
    const e = energy[i] ?? 0;
    // A cell a bare picture left out has nothing to lift.
    if (e < 0.02 || picture.drawn[i] === 0) {
      energy[i] = 0;
      continue;
    }
    any = true;
    const step = picture.steps[i] ?? 0;
    ctx.globalAlpha = Math.min(1, e * 2.4);
    ctx.fillStyle =
      palette.shape === "square"
        ? (palette.ramp[Math.max(0, step - 3)] ?? palette.bg)
        : palette.bg;
    ctx.fillRect(
      (i % grid.cols) * grid.cell,
      Math.floor(i / grid.cols) * grid.cell,
      grid.cell + 0.6,
      grid.cell + 0.6,
    );
  }
  ctx.globalAlpha = 1;
  if (!any) return false;

  const decay = Math.exp(-dt * SETTLE);
  for (let i = 0; i < energy.length; i++) {
    const e = energy[i] ?? 0;
    if (e === 0) continue;
    const a = angle[i] ?? 0;
    const push = e * grid.cell * 0.85;
    const cx = ((i % grid.cols) + 0.5) * grid.cell + Math.cos(a) * push;
    const cy = (Math.floor(i / grid.cols) + 0.5) * grid.cell + Math.sin(a) * push;
    const step = picture.steps[i] ?? 0;
    const k = Math.min(last, step + Math.round(e * 2));
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(a);
    if (palette.shape === "square") {
      leaf(ctx, grid.cell, e, { fill: palette.ramp[k] ?? palette.bg, tip: palette.tip });
    } else {
      stroke(ctx, grid.cell, e, {
        colour: palette.ramp[Math.min(last, k + 1)] ?? palette.bg,
        weight: step / last,
      });
    }
    ctx.restore();
    energy[i] = e * decay;
  }
  return true;
}

function leaf(
  ctx: CanvasRenderingContext2D,
  cell: number,
  e: number,
  { fill, tip }: { readonly fill: string; readonly tip: string },
): void {
  const length = cell * 0.5 * (1 + 2.3 * e);
  const width = cell * 0.5 * (1 - 0.5 * e);
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(-length, 0);
  ctx.quadraticCurveTo(-length * 0.1, -width * 1.35, length, 0);
  ctx.quadraticCurveTo(-length * 0.1, width * 1.35, -length, 0);
  ctx.fill();
  ctx.globalAlpha = 0.75 * e;
  ctx.fillStyle = tip;
  ctx.beginPath();
  ctx.moveTo(length * 0.15, -width * 0.28);
  ctx.lineTo(length, 0);
  ctx.lineTo(length * 0.15, width * 0.28);
  ctx.fill();
  ctx.globalAlpha = 1;
}

function stroke(
  ctx: CanvasRenderingContext2D,
  cell: number,
  e: number,
  { colour, weight }: { readonly colour: string; readonly weight: number },
): void {
  const length = cell * 0.5 * (1 + 2.3 * e);
  ctx.strokeStyle = colour;
  ctx.lineCap = "round";
  ctx.lineWidth = cell * (0.2 + 0.22 * weight);
  ctx.beginPath();
  ctx.moveTo(-length * 0.75, 0);
  ctx.lineTo(length * 0.75, 0);
  ctx.stroke();
}
